import React, { useEffect, useRef, useState } from 'react'
import { PlayCircleOutlined, PauseCircleOutlined } from '@ant-design/icons';
import ActionArea from '../../components/ActionArea';
import SelectInput from '../../components/UI/SelectInput';
import { deleteMigrationById, getMigrationDetailsById, getMigrationStatus, getRunningMigration, starOrtStopMigration } from '../../services/migrationStatus';
import { useLoading } from '../../context/LoadingProvider/useLoading';
import List from '../../components/List';
import { Divider, Modal, Tag } from 'antd';
import Button from '../../components/UI/Button';
import { notificationError } from '../../services/notification';
import { useNavigate } from 'react-router-dom';
import direction from '../../utils/filterDirectionOrder';
import { compareDates, stringCompare } from '../../utils/sorters';
import { sendSystemLog, useAuth } from '../../context/AuthProvider/useAuth';
import dayjs from "dayjs";

const dataRangeOptions = [
  {name: 'last day', value: 'last day'},
  {name: 'last week', value: 'last week'},
  {name: 'last month', value: 'last month'},
  {name: 'last year', value: 'last year'},
  {name: 'all', value: 'all'},
]
const REFRESH_TIME = 1 * 1000

export interface IMigrationStats {
  queued: number,
  running: number,
  cancelled: number,
  failed: number,
  finished: number
}

export interface IMigrationHistory {
  queuedId: string,
  timestamp: string,
  elapsed: string,
  distributor: string,
  location: string,
  from: string,
  to: string,
  status: string,
  phase: null | string,
  elapsedLoad: 0.0,
  elapsedPrepare: 0.0,
  elapsedMove: 0.0,
  error: null | string
}

function index() {
  const loading = useLoading()
  const history = useNavigate()
  const ref = useRef<any | null>(null)
  const log = {
    log_type: 'visited',
    log_object: 'migration_status'
  }
  const {canEdit} = useAuth();
  const [dataRange, setDataRange] = useState(dataRangeOptions[0].value)
  const [migrationStats, setMigrationStats] = useState<IMigrationStats>({
    queued: 0,
    running: 0,
    cancelled: 0,
    failed: 0,
    finished: 0
  })
  const [migrationHistory, setMigrationHistory] = useState<IMigrationHistory[]>([])
  const [migrationStatus, setMigrationStatus] = useState<boolean>(false)

  const startDisabled = migrationStatus || !canEdit || !migrationHistory.find((migration) => migration.status.toUpperCase() === 'QUEUED')
  const stopDisabled = !migrationStatus || !canEdit

  const startStopMigration = async (type: 'start' | 'stop') => {
    loading.addLoading()
    await starOrtStopMigration(type)
    const res = await getRunningMigration()
    setMigrationStatus(res.enabled)
    loading.removeLoading()
  }

  useEffect(() => {
    sendSystemLog(log);
  }, [])

  useEffect(()=>{
    (async ()=>{
      loading.addLoading()
      const res = await getRunningMigration()
      setMigrationStatus(res.enabled)
      refresh()
      loading.removeLoading()
    })()
  },[])

  useEffect(()=>{
    loading.addLoading()
    ref.current = setInterval(refresh, REFRESH_TIME);
    const timeoutId = setTimeout(() => {
      loading.removeLoading();
    }, 1000);
    return () => {
      if(ref.current) clearInterval(ref.current)
    }
  },[dataRange, migrationStatus])

  const refresh = async () => {
    const res = await getMigrationStatus({visibleRange: dataRange})
    if(res.migrationStats.running === 0) {
      const res = await getRunningMigration()
      setMigrationStatus(res.enabled)
    }
    setMigrationStats(res.migrationStats)
    setMigrationHistory(res.migrationHistory)
  }

  return (
    <div className="migration-status">
      <div className="migration-status__controls">
        <button className="migration-status__controls__start" disabled={startDisabled} onClick={() => startStopMigration('start')}>
          START <PlayCircleOutlined />
        </button>
        <button className="migration-status__controls__stop" disabled={stopDisabled} onClick={() => startStopMigration('stop')}>
          STOP <PauseCircleOutlined />
        </button>
      </div>
      <div className="migration-status__status">
        <div className="migration-status__status__content">
          <Tag color="blue">QUEUED: {migrationStats.queued}</Tag>
          <Tag color="lime">RUNNING: {migrationStats.running}</Tag>
          <Tag color="red">FAILED: {migrationStats.failed}</Tag>
          <Tag color="orange">CANCELLED: {migrationStats.cancelled}</Tag>
          <Tag color="green">FINISHED: {migrationStats.finished}</Tag>
        </div>

      </div>
      <ActionArea buttonAction={() => history('/migrationqueue')} buttonText='Go to migration Queue'>
        <SelectInput 
          label={'Visible data range'}
          onSelectChange={(e) => {e?.value ? setDataRange(e.value) : setDataRange(e)}}
          options={dataRangeOptions}
          value={dataRange}
          selectOnEnter={true}
        />
      </ActionArea>
      <List
        rows={migrationHistory}
        pagination={false}
        columns={[
          {
            title: 'Start time',
            dataIndex: 'timestamp',
            key: 'timestamp',
            render: (value: string) => dayjs(value).format('YYYY-MM-DD HH:mm:ss') || '--',
            width: 100,
            sorter: (a: IMigrationHistory, b: IMigrationHistory) => compareDates(dayjs(a.timestamp).format('YYYY-MM-DD HH:mm:ss'), dayjs(b.timestamp).format('YYYY-MM-DD HH:mm:ss'), 'YYYY-MM-DD HH:mm:ss')
          },
          {
            title: 'Elapsed',
            dataIndex: 'elapsed',
            key: 'elapsed',
            render: (value: string) => value,
            width: 100,
            sorter: (a: IMigrationHistory, b: IMigrationHistory) => compareDates(dayjs(a.elapsed, 'HH:mm:ss').format(), dayjs(b.elapsed, 'HH:mm:ss').format(), 'HH:mm:ss')
          },
          {
            title: 'Distributor',
            dataIndex: 'distributor',
            key: 'distributor',
            render: (value: string) => value,
            width: 100,
            sorter: (a: IMigrationHistory, b: IMigrationHistory) => stringCompare(a.distributor, b.distributor)
          },
          {
            title: 'Location',
            dataIndex: 'location',
            key: 'location',
            render: (value: string) => value,
            width: 100,
            sorter: (a: IMigrationHistory, b: IMigrationHistory) => stringCompare(a.location, b.location)
          },
          {
            title: 'From',
            dataIndex: 'from',
            key: 'from',
            render: (value: string) => value,
            width: 100,
            sorter: (a: IMigrationHistory, b: IMigrationHistory) => stringCompare(a.from, b.from)
          },
          {
            title: 'To',
            dataIndex: 'to',
            key: 'to',
            render: (value: string) => value,
            width: 100,
            sorter: (a: IMigrationHistory, b: IMigrationHistory) => stringCompare(a.to, b.to)
          },
          {
            title: 'State',
            dataIndex: 'status',
            key: 'status',
            render: (value: string, record: IMigrationHistory) => (
              <>
                <StatusTag type={value}/>
                <br/>
                <PhaseTag phase={record.phase} status={value}/>
              </>
            ),
            sorter: (a: IMigrationHistory, b: IMigrationHistory) => stringCompare(a.status, b.status),
            width: 100
          },
          {
            title: 'Action',
            dataIndex: 'action',
            key: 'action',
            render: (value: string, record: IMigrationHistory) => <ActionControls migration={record} refresh={refresh} isMigrating={migrationStatus}/>,
            width: 100
          },
        ]}
      />
    </div>
  )
}


const StatusTag = (props: {type: string}) => {
  if(props.type.toUpperCase() === 'QUEUED') return <Tag color="blue">Queued</Tag>
  if(props.type.toUpperCase() === 'RUNNING') return <Tag color="lime">Running</Tag>
  if(props.type.toUpperCase() === 'FAILED') return <Tag color="red">Failed</Tag>
  if(props.type.toUpperCase() === 'CANCELLED') return <Tag color="orange">Canceled</Tag>
  if(props.type.toUpperCase() === 'FINISHED') return <Tag color="green">Finished</Tag>
  return <Tag color="blue">{props.type}</Tag>
}

const PhaseTag = (props: {phase: string | null, status: string}) => {
  if(!props.phase || props.status.toUpperCase() === 'CANCELLED' || props.status.toUpperCase() === 'FAILED') return <></>
  if(props.phase.toUpperCase() === 'LOADING') return <Tag color="cyan">Loading</Tag>
  if(props.phase.toUpperCase() === 'PREPARING') return <Tag color="blue">Preparing</Tag>
  if(props.phase.toUpperCase() === 'MOVING') return <Tag color="geekblue">Moving</Tag>
  if(props.phase.toUpperCase() === 'FINISHED') return <></>
  return <Tag color="blue">{props.phase}</Tag>
}

const ActionControls = (props: {migration: IMigrationHistory, refresh: () => void, isMigrating: boolean}) => {
  const {migration} = props;
  const {canEdit} = useAuth();
  const deleteMigration = async (id: string) => {
    if(!id) return notificationError('Could not find migration')
    await deleteMigrationById({id})
    props.refresh()
  }

  const migrationDetails = async (id: string) => {
    if(!id) return notificationError('Could not find migration')
    const res = await getMigrationDetailsById({id})
    Modal.info({
      title: 'Migration Details',
      okText: 'Close',
      centered: true,
      content: res.detailMsg,
    });
  }

  if(migration.status.toUpperCase() === 'QUEUED') return (
    <Button
      disabled={props.isMigrating || !canEdit}
      onClick={() => deleteMigration(migration.queuedId)}
      style={{backgroundColor: '#bd4247' , minWidth: '100px'}}
      type='primary'
    >
      <p>Remove</p>
    </Button>
  )

  if(migration.status.toUpperCase() === 'FAILED' || migration.status.toUpperCase() === 'CANCELLED') return (
    <Button
      onClick={() => migrationDetails(migration.queuedId)}
      style={{backgroundColor: '#2fa4e7' , minWidth: '100px'}}
      type='primary'
    >
      <p>Details</p>
    </Button>
  )
  return <></>
}

export default index